/* eslint-disable no-nested-ternary */
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	Icon,
	List,
	ListItem,
	ListItemText
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MDBadge from "components/Basics/MDBadge";
import MDBox from "components/Basics/MDBox";
import MDButton from "components/Basics/MDButton";
import MDTypography from "components/Basics/MDTypography";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import DeploymentActions from "redux-react/actions/deploymentActions";
import { socket } from "redux-react/middleware/ws";

/**
 * Compact section header: title + count badge, used across all anomaly sections.
 */
const SectionHeader = ({ title, count, isEmpty }) => (
	<MDBox display="flex" alignItems="center" gap={1}>
		<MDTypography
			variant="button"
			fontWeight="bold"
			sx={{ textTransform: "uppercase", fontSize: 11 }}
		>
			{title}
		</MDTypography>
		<MDBadge
			size="xs"
			container
			variant="contained"
			color={isEmpty ? "success" : "error"}
			badgeContent={count}
		/>
	</MDBox>
);

/**
 * One expandable anomaly row. Lazy-loads its Mongo detail (name + metadatas) on
 * first expand. `detailsType` may be null when no drill-down is available
 * (e.g. orphan vectors that exist only in the vector DB).
 */
const AnomalyItem = ({ accountID, assistantID, label, id, detailsType, data }) => {
	const dispatch = useDispatch();
	const [expanded, setExpanded] = useState(false);
	const [loading, setLoading] = useState(false);
	const [detail, setDetail] = useState(null);
	const [loaded, setLoaded] = useState(false);

	const handleChange = (_, isExpanded) => {
		setExpanded(isExpanded);
		if (isExpanded && !loaded && detailsType && id) {
			setLoading(true);
			dispatch(
				DeploymentActions.dataCheckDetails(
					assistantID,
					{ ...data, accountID, type: detailsType, ids: [id] },
					res => {
						const items = res?.data?.items ?? [];
						setDetail(items[0] ?? null);
						setLoading(false);
						// setLoaded(true);
					},
					() => {
						setLoading(false);
						// setLoaded(true);
					}
				)
			);
		}
	};

	const onVectorDetailsResult = data => {
		if (data?.assistantID && assistantID && data.assistantID !== assistantID) {
			return;
		}

		const success = data?.success ?? false;
		if (!success) {
			return;
		}

		const vectors = data?.vectors ?? [];
		const vector = vectors[0];

		if (!vector) {
			return;
		}

		setDetail(vector?.metadatas);
	};

	useEffect(() => {
		socket.on("vector_data_details_result", onVectorDetailsResult);
		return () => {
			socket.off("vector_data_details_result", onVectorDetailsResult);
		};
	}, [assistantID]);

	return (
		<Accordion
			expanded={expanded}
			onChange={handleChange}
			disableGutters
			sx={{ bgcolor: "transparent", boxShadow: "none", "&:before": { display: "none" } }}
		>
			<AccordionSummary
				expandIcon={detailsType ? <ExpandMoreIcon /> : null}
				sx={{ minHeight: 32, "& .MuiAccordionSummary-content": { my: 0.5 } }}
			>
				<MDTypography variant="body2">{label}</MDTypography>
			</AccordionSummary>
			{detailsType && (
				<AccordionDetails sx={{ bgcolor: "grey.200", borderRadius: 1 }}>
					{loading ? (
						<MDBox display="flex" alignItems="center">
							<CircularProgress size={14} sx={{ mr: 1 }} />
							<MDTypography variant="caption">{t("CONFIG.dataCheck.loadingDetails")}</MDTypography>
						</MDBox>
					) : detail ? (
						<MDBox
							component="pre"
							sx={{
								m: 0,
								fontSize: 11,
								whiteSpace: "pre-wrap",
								wordBreak: "break-word",
								maxHeight: 240,
								overflow: "auto"
							}}
						>
							{JSON.stringify(detail, null, 2)}
						</MDBox>
					) : (
						<MDTypography variant="caption">{t("CONFIG.dataCheck.notFound")}</MDTypography>
					)}
				</AccordionDetails>
			)}
		</Accordion>
	);
};

/**
 * A section listing one type of anomaly. Shows a green "no anomaly" state when empty.
 *
 * @param {Array} items raw items (string uuid OR object with code/uuid)
 * @param {(item) => string} renderItem label renderer
 * @param {(item) => string} getId extracts the lookup id for drill-down (returns null to disable)
 * @param {string|null} detailsType "source" | "knowledge" | null
 */
const AnomalySection = ({
	accountID,
	assistantID,
	title,
	items,
	renderItem,
	getId,
	detailsType,
	data
}) => {
	const isEmpty = !items || items.length === 0;
	return (
		<MDBox mb={1.5}>
			<MDBox mb={0.75}>
				<SectionHeader title={title} count={isEmpty ? "0" : items.length} isEmpty={isEmpty} />
			</MDBox>
			{isEmpty ? (
				<MDBox display="flex" flexDirection="row" alignItems="center">
					<Icon color="success" fontSize="small" sx={{ mr: 0.75, fontSize: 16 }}>
						check_circle
					</Icon>
					<MDTypography variant="caption" color="text">
						{t("CONFIG.dataCheck.noAnomaly")}
					</MDTypography>
				</MDBox>
			) : (
				<List
					dense
					disablePadding
					sx={{
						maxHeight: 220,
						overflow: "auto",
						bgcolor: "grey.100",
						borderRadius: 1,
						border: "1px solid",
						borderColor: "grey.300"
					}}
				>
					{items.map((item, index) => {
						const id = getId ? getId(item) : null;
						return (
							<ListItem key={index} divider sx={{ display: "block", py: 0 }}>
								{detailsType && id ? (
									<AnomalyItem
										accountID={accountID}
										assistantID={assistantID}
										label={renderItem(item)}
										id={id}
										detailsType={detailsType}
										data={data}
									/>
								) : (
									<ListItemText
										primaryTypographyProps={{ variant: "body2" }}
										primary={renderItem(item)}
									/>
								)}
							</ListItem>
						);
					})}
				</List>
			)}
		</MDBox>
	);
};

/**
 * Data integrity check dialog.
 * - Immediately calls GET /faiq/dataCheck/:assistantID and renders the Mongo report (HTTP 202).
 * - Listens to the `faiq_data_check_result` websocket event for the asynchronous vector (Pinecone) result.
 * - Supports per-item drill-down (sources / knowledges) and a Patch action to fix all anomalies at once.
 */
const DataCheckDialog = ({ open, onClose, accountID, assistantID, resourceConfig = {}, data }) => {
	const [vectorReport, setVectorReport] = useState(null);

	// Patch state
	const [patching, setPatching] = useState(false);
	const [patchVectorResult, setPatchVectorResult] = useState(null);
	const [patchMongoResult, setPatchMongoResult] = useState(null);

	const onDataCheckResult = data => {
		// Guard: only handle results for the assistant we are checking
		if (data?.assistantID && assistantID && data.assistantID !== assistantID) {
			return;
		}

		if (data?.stage === "vector") {
			setVectorReport(data);
		}
	};

	useEffect(() => {
		socket.on("bijection_data_check_result", onDataCheckResult);
		return () => {
			socket.off("bijection_data_check_result", onDataCheckResult);
		};
	}, [assistantID]);

	const handleClose = () => {
		setVectorReport(null);
		setPatching(false);
		setPatchVectorResult(null);
		setPatchMongoResult(null);
		onClose();
	};

	const renderVectorPart = () => {
		// vector model not found on the vector component side
		if (vectorReport && vectorReport.success === false) {
			return (
				<MDBox bgColor="error" p={2} borderRadius="lg" textAlign="center">
					<MDTypography variant="button" color="white">
						{vectorReport.error === "vectorModelNotFound"
							? t("CONFIG.dataCheck.vectorModelNotFound")
							: t("CONFIG.dataCheck.error")}
					</MDTypography>
				</MDBox>
			);
		}

		// vector result not received yet
		if (!vectorReport) {
			return (
				<MDBox display="flex" flexDirection="row" alignItems="center">
					<CircularProgress size={18} color="info" sx={{ mr: 2 }} />
					<MDTypography variant="body2" color="text">
						{t("CONFIG.dataCheck.vectorPending")}
					</MDTypography>
				</MDBox>
			);
		}

		return (
			<>
				<MDBox display="flex" flexDirection="row" gap={1} mb={1.5}>
					<MDBox
						px={1.5}
						py={0.5}
						borderRadius="md"
						sx={{ bgcolor: "grey.100", border: "1px solid", borderColor: "grey.300" }}
					>
						<MDTypography variant="caption" color="text">
							{t("CONFIG.dataCheck.totalMongo")}:{" "}
							<MDTypography variant="caption" fontWeight="bold" color="dark" component="span">
								{vectorReport.totalMongo ?? "-"}
							</MDTypography>
						</MDTypography>
					</MDBox>
					<MDBox
						px={1.5}
						py={0.5}
						borderRadius="md"
						sx={{ bgcolor: "grey.100", border: "1px solid", borderColor: "grey.300" }}
					>
						<MDTypography variant="caption" color="text">
							{t("CONFIG.dataCheck.totalVectorDB")}:{" "}
							<MDTypography variant="caption" fontWeight="bold" color="dark" component="span">
								{vectorReport.totalVectorDB ?? "-"}
							</MDTypography>
						</MDTypography>
					</MDBox>
				</MDBox>
				<AnomalySection
					accountID={accountID}
					assistantID={assistantID}
					title={t("CONFIG.dataCheck.missingInVectorDB")}
					items={vectorReport.missingInVectorDB}
					renderItem={uuid => uuid}
					getId={uuid => uuid}
					detailsType="knowledge"
					data={data}
				/>
				<AnomalySection
					accountID={accountID}
					assistantID={assistantID}
					title={t("CONFIG.dataCheck.orphansInVectorDB")}
					items={vectorReport.orphansInVectorDB}
					renderItem={uuid => uuid}
					getId={uuid => uuid}
					detailsType="vector"
					data={data}
				/>
			</>
		);
	};

	const renderPatchStatus = () => {
		if (!patching && !patchMongoResult && !patchVectorResult) return null;
		return (
			<MDBox
				mt={2}
				p={2}
				borderRadius="lg"
				sx={{ bgcolor: patchVectorResult?.success === false ? "error.light" : "info.light" }}
			>
				{patching && !patchVectorResult && (
					<MDBox display="flex" alignItems="center" mb={1}>
						<CircularProgress size={16} sx={{ mr: 1 }} />
						<MDTypography variant="body2">{t("CONFIG.dataCheck.patchPending")}</MDTypography>
					</MDBox>
				)}
				{patchMongoResult && (
					<MDTypography variant="caption" display="block">
						{t("CONFIG.dataCheck.patchMongoDone", {
							sourcesDeleted: patchMongoResult.sourcesDeleted ?? 0,
							knowledgesDeleted: patchMongoResult.knowledgesDeleted ?? 0
						})}
					</MDTypography>
				)}
				{patchVectorResult && (
					<MDTypography variant="caption" display="block">
						{patchVectorResult.success === false
							? t("CONFIG.dataCheck.patchError")
							: t("CONFIG.dataCheck.patchVectorDone", {
									vectorized: patchVectorResult.vectorized ?? 0,
									vectorsDeleted: patchVectorResult.vectorsDeleted ?? 0,
									failures: patchVectorResult.vectorizeFailures ?? 0
								})}
					</MDTypography>
				)}
			</MDBox>
		);
	};

	const renderContent = () => {
		return (
			<MDBox display="flex" flexDirection="column">
				<MDBox mt={2} mb={1.5} pb={0.5}>
					<MDTypography variant="subtitle2" fontWeight="bold" color="dark">
						Ressource
					</MDTypography>
					<MDBox
						component="pre"
						sx={{
							m: 0,
							fontSize: 11,
							whiteSpace: "pre-wrap",
							wordBreak: "break-word",
							maxHeight: 240,
							overflow: "auto"
						}}
					>
						{JSON.stringify(
							{
								models: resourceConfig?.models,
								pinecone: resourceConfig?.pinecone
							},
							null,
							2
						)}
					</MDBox>
				</MDBox>
				<MDBox
					mt={2}
					mb={1.5}
					pb={0.5}
					sx={{ borderBottom: "2px solid", borderColor: "info.main" }}
				>
					<MDTypography variant="subtitle2" fontWeight="bold" color="dark">
						{t("CONFIG.dataCheck.vectorSection")}
					</MDTypography>
				</MDBox>
				<MDBox>{renderVectorPart()}</MDBox>

				{renderPatchStatus()}
			</MDBox>
		);
	};

	return (
		<>
			<Dialog fullWidth maxWidth="xxl" open={open} onClose={handleClose}>
				<MDBox
					display="flex"
					flexDirection="row"
					justifyContent="space-between"
					alignItems="center"
					px={3}
					py={1.5}
					sx={{ bgcolor: "grey.100", borderBottom: "1px solid", borderColor: "grey.300" }}
				>
					<MDBox display="flex" alignItems="center" gap={1}>
						<Icon color="info">fact_check</Icon>
						<MDTypography variant="h6" fontWeight="bold">
							{t("CONFIG.dataCheck.title")}
						</MDTypography>
					</MDBox>
					<MDBox display="flex" flexDirection="row" gap={1}>
						{/* <MDButton
							variant="outlined"
							color="info"
							size="small"
							disabled={loadingMongo || patching}
							onClick={startCheck}
						>
							<Icon sx={{ mr: 0.5 }}>refresh</Icon>
							{t("CONFIG.dataCheck.relaunch")}
						</MDButton> */}
						<MDButton variant="contained" color="info" size="small" onClick={handleClose}>
							{t("SETTINGS.close")}
						</MDButton>
					</MDBox>
				</MDBox>
				<DialogContent sx={{ pt: 2 }}>{renderContent()}</DialogContent>
			</Dialog>

			{/* <PatchConfirmDialog
				open={confirmOpen}
				onClose={() => setConfirmOpen(false)}
				onConfirm={launchPatch}
				counts={patchCounts}
			/> */}
		</>
	);
};

export default DataCheckDialog;
